import React from 'react';
import TestResultDisplay from '../../common/TestResultDisplay';

// 测试项目接口
interface TestItem {
  name: string;
  passed: boolean;
  userAnswer?: string;
  correctAnswer?: string;
  details?: string;
}

// 测试组接口
interface TestGroup {
  title: string;
  subtitle?: string;
  score?: { current: number; total: number };
  items: TestItem[];
  passed: boolean;
  note?: string;
  hasLogicalError?: boolean;
  errorReason?: string;
}

interface VisionSummaryDisplayProps {
  responses: any;
  visionStatus?: string;  // 视力评估状态
  className?: string;
}

const VisionSummaryDisplay: React.FC<VisionSummaryDisplayProps> = ({ 
  responses, 
  visionStatus,
  className = '' 
}) => {

  // 判断视力是否通过 - 基于传入的状态
  const visionPassed = visionStatus === 'PASS';
  
  // 从真实的responses数据中获取视力评估数据
  const getRealVisionData = () => {
    // 方式1: 从直接字段获取
    if (responses?.filterQuestion !== undefined || responses?.externalEyeCheckPassed !== undefined || responses?.rightEyeLargeECorrect !== undefined) {
      return {
        filterQuestion: responses.filterQuestion,
        symptomsFilter: responses.symptomsFilter,
        riskFactorsFilter: responses.riskFactorsFilter,
        externalEyeCheckPassed: responses.externalEyeCheckPassed,
        rightEyeLargeECorrect: responses.rightEyeLargeECorrect,
        rightEyeLargeEPassed: responses.rightEyeLargeEPassed,
        rightEyeMediumECount: responses.rightEyeMediumECount,
        rightEyeMediumEPassed: responses.rightEyeMediumEPassed,
        leftEyeLargeECorrect: responses.leftEyeLargeECorrect,
        leftEyeLargeEPassed: responses.leftEyeLargeEPassed,
        leftEyeMediumECount: responses.leftEyeMediumECount,
        leftEyeMediumEPassed: responses.leftEyeMediumEPassed,
        nearVisionECount: responses.nearVisionECount,
        nearVisionPassed: responses.nearVisionPassed
      };
    }
    
    // 方式2: 从 testResults 中获取 rawUserAnswers，合并所有视力测试数据
    if (responses?.testResults) {
      const visionTests = responses.testResults.filter((test: any) => 
        test.name === "视力问题筛查" || test.name === "外部眼检" || 
        test.name === "右眼大E字符测试 (6/60)" || test.name === "左眼大E字符测试 (6/60)" || 
        test.name === "右眼中号E字符测试 (6/12)" || test.name === "左眼中号E字符测试 (6/12)" || 
        test.name === "近视力测试 (N6)"
      );
      
      if (visionTests.length > 0) {
        // 合并所有视力测试的rawUserAnswers数据
        const combinedData: any = {};
        visionTests.forEach((test: any) => {
          if (test.rawUserAnswers) {
            // 使用 Object.assign 确保完整合并
            Object.assign(combinedData, test.rawUserAnswers);
          }

          // 🎯 修复：不再从测试结果推断filterQuestion，而是从rawUserAnswers中获取真实用户回答
          if (test.name === "视力问题筛查" && test.rawUserAnswers) {
            // 从rawUserAnswers中获取用户的真实回答
            if (test.rawUserAnswers.filterQuestion !== undefined) {
              combinedData.filterQuestion = test.rawUserAnswers.filterQuestion;
            }
          }
        });

        if (Object.keys(combinedData).length > 0) {
          return combinedData;
        }
      }
    }
    
    // 方式3: 从domainResult中的testResults获取 (QuickTestResultPage的数据结构修复)
    // 这是关键修复：QuickTestResultPage应该传递完整的domainResult而不只是responses
    if ((responses as any)?.status && (responses as any)?.testResults) {
      const visionTests = (responses as any).testResults.filter((test: any) => 
        test.name === "外部眼检" || test.name === "右眼大E字符测试" || test.name === "左眼大E字符测试" || 
        test.name === "右眼中E字符测试" || test.name === "左眼中E字符测试" || test.name === "近视力E字符测试"
      );
      
      if (visionTests.length > 0) {
        // 合并所有视力测试的rawUserAnswers数据
        const combinedData: any = {};
        visionTests.forEach((test: any) => {
          if (test.rawUserAnswers) {
            // 使用 Object.assign 确保完整合并
            Object.assign(combinedData, test.rawUserAnswers);
          }

          // 🎯 修复：不再从测试结果推断filterQuestion，而是从rawUserAnswers中获取真实用户回答
          if (test.name === "视力问题筛查" && test.rawUserAnswers) {
            // 从rawUserAnswers中获取用户的真实回答
            if (test.rawUserAnswers.filterQuestion !== undefined) {
              combinedData.filterQuestion = test.rawUserAnswers.filterQuestion;
            }
          }
        });

        if (Object.keys(combinedData).length > 0) {
          return combinedData;
        }
      }
    }

    return null;
  };
  
  // 动态生成视力状况测试数据，基于真实数据
  const generateVisionTestData = (): TestGroup[] => {
    const realData = getRealVisionData();

    // 如果没有真实数据，显示基础状态信息
    if (!realData) {
      return [
        {
          title: '视力状况评估',
          passed: visionPassed,
          items: [
            {
              name: '评估状态',
              passed: visionPassed,
              userAnswer: visionPassed ? '视力状况正常' : '视力状况需要关注',
              details: '数据不可用，基于系统评估结果'
            }
          ]
        }
      ];
    }

    const testGroups: TestGroup[] = [];
    
    
    // 眼部外观评估 - 基于真实数据
    if (realData.externalEyeCheckPassed !== undefined) {
      testGroups.push({
        title: '眼部外观检查',
        passed: realData.externalEyeCheckPassed,
        items: [
          {
            name: '眼部外观检查',
            passed: realData.externalEyeCheckPassed,
            userAnswer: realData.externalEyeCheckPassed ? '正常，无异常' : '发现异常'
          }
        ]
      });
    }
    
    // 右眼远视力评估 - 基于真实数据
    if (realData.rightEyeLargeECorrect !== undefined || realData.rightEyeMediumECount !== undefined) {
      const items: TestItem[] = [];
      
      if (realData.rightEyeLargeECorrect !== undefined) {
        items.push({
          name: '大E字符测试 (6/60)',
          passed: realData.rightEyeLargeEPassed,
          userAnswer: `${realData.rightEyeLargeECorrect}/4个 E字符方向 (${realData.rightEyeLargeEPassed ? '通过' : '未通过'})`
        });
      }
      
      // 只有当大E字符测试通过时，才显示中号E字符测试结果（符合WHO ICOPE测试逻辑）
      if (realData.rightEyeMediumECount !== undefined && realData.rightEyeMediumECount !== null && realData.rightEyeLargeEPassed) {
        items.push({
          name: '中号E字符测试 (6/12)',
          passed: realData.rightEyeMediumEPassed,
          userAnswer: `${realData.rightEyeMediumECount}/5个 E字符方向 (${realData.rightEyeMediumEPassed ? '通过' : '未通过'})`
        });
      }
      
      if (items.length > 0) {
        // 右眼整体通过状态：大E通过且（中号E通过或未测试中号E）
        // 修复：同时检查null和undefined，覆盖所有未测试场景
        const rightEyePassed = realData.rightEyeLargeEPassed &&
          (realData.rightEyeMediumECount === null ||
           realData.rightEyeMediumECount === undefined ||
           realData.rightEyeMediumEPassed);
        testGroups.push({
          title: '右眼远视力测试',
          passed: rightEyePassed,
          items: items
        });
      }
    }
    
    // 左眼远视力评估 - 基于真实数据
    if (realData.leftEyeLargeECorrect !== undefined || realData.leftEyeMediumECount !== undefined) {
      const items: TestItem[] = [];
      
      if (realData.leftEyeLargeECorrect !== undefined) {
        items.push({
          name: '大E字符测试 (6/60)',
          passed: realData.leftEyeLargeEPassed,
          userAnswer: `${realData.leftEyeLargeECorrect}/4个 E字符方向 (${realData.leftEyeLargeEPassed ? '通过' : '未通过'})`
        });
      }
      
      // 只有当大E字符测试通过时，才显示中号E字符测试结果（符合WHO ICOPE测试逻辑）
      if (realData.leftEyeMediumECount !== undefined && realData.leftEyeMediumECount !== null && realData.leftEyeLargeEPassed) {
        items.push({
          name: '中号E字符测试 (6/12)',
          passed: realData.leftEyeMediumEPassed,
          userAnswer: `${realData.leftEyeMediumECount}/5个 E字符方向 (${realData.leftEyeMediumEPassed ? '通过' : '未通过'})`
        });
      }
      
      if (items.length > 0) {
        // 左眼整体通过状态：大E通过且（中号E通过或未测试中号E）
        // 修复：同时检查null和undefined，覆盖所有未测试场景
        const leftEyePassed = realData.leftEyeLargeEPassed &&
          (realData.leftEyeMediumECount === null ||
           realData.leftEyeMediumECount === undefined ||
           realData.leftEyeMediumEPassed);
        testGroups.push({
          title: '左眼远视力测试',
          passed: leftEyePassed,
          items: items
        });
      }
    }
    
    // 近视力评估 - 基于真实数据
    if (realData.nearVisionECount !== undefined) {
      testGroups.push({
        title: '近视力测试',
        passed: realData.nearVisionPassed,
        items: [
          {
            name: '近视力测试 (N6)',
            passed: realData.nearVisionPassed,
            userAnswer: `${realData.nearVisionECount}/5个 E字符方向 (${realData.nearVisionPassed ? '通过' : '未通过'})`
          }
        ]
      });
    }

    // 如果没有任何真实测试数据，显示状态信息
    if (testGroups.length === 0) {
      testGroups.push({
        title: '视力状况评估',
        passed: visionPassed,
        items: [
          {
            name: '评估结果',
            passed: visionPassed,
            userAnswer: visionPassed ? '视力状况正常' : '视力状况需要关注',
            details: '基于WHO ICOPE视力评估标准'
          }
        ]
      });
    }

    return testGroups;
  };

  const testGroups = generateVisionTestData();
  const passedCount = testGroups.filter(group => group.passed).length;
  const totalCount = testGroups.length;

  return (
    <TestResultDisplay
      title="视力评估结果"
      subtitle={`综合评估: ${passedCount}/${totalCount}项指标正常`}
      testGroups={testGroups}
      className={className}
    />
  );
};

export default VisionSummaryDisplay;